package de.xtl.retroguide.question

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.MutableTransitionState
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.slideIn
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import de.xtl.retroguide.formatOneDecimal
import de.xtl.retroguide.protocol.AnswerCategory
import de.xtl.retroguide.protocol.QuestionWidget
import de.xtl.retroguide.protocol.VisibleAnswer
import de.xtl.retroguide.protocol.categories
import de.xtl.retroguide.protocol.toCategorizedAnswer
import de.xtl.retroguide.question.scenes.CategoryScene
import de.xtl.retroguide.question.scenes.VotingRanking
import de.xtl.retroguide.protocol.AnswerCluster
import de.xtl.retroguide.protocol.VotingOption
import de.xtl.retroguide.question.scenes.RadarChart
import de.xtl.retroguide.question.scenes.RotiGauge
import de.xtl.retroguide.rotiStats
import org.jetbrains.compose.resources.stringResource
import retroguide.app.shared.generated.resources.Res
import retroguide.app.shared.generated.resources.roti_average
import retroguide.app.shared.generated.resources.roti_vote_single
import retroguide.app.shared.generated.resources.roti_votes

private const val STAGGER_MILLIS = 70
private const val MAX_STAGGER_STEPS = 10

@Composable
internal fun AnswerBoard(
    answers: List<VisibleAnswer>,
    widget: QuestionWidget,
    clusters: List<AnswerCluster> = emptyList(),
    votingOptions: List<VotingOption> = emptyList(),
    onDeleteCluster: ((String) -> Unit)? = null,
    item: @Composable (VisibleAnswer) -> Unit,
) {
    when {
        widget == QuestionWidget.DOT_VOTING -> VotingRanking(votingOptions, answers)
        widget.categories.isNotEmpty() -> CategoryBoard(answers, widget, item) {
            ClusterSection(clusters, answers, onDeleteCluster)
        }
        widget == QuestionWidget.ROTI -> {
            RotiHistogram(answers)
            answers.forEach { answer -> key(answer.id) { item(answer) } }
        }
        widget == QuestionWidget.TEAM_RADAR -> {
            RadarChart(answers)
            answers.forEach { answer -> key(answer.id) { item(answer) } }
        }
        else -> {
            ClusterSection(clusters, answers, onDeleteCluster)
            answers.forEach { answer -> key(answer.id) { item(answer) } }
        }
    }
}

@Composable
private fun CategoryBoard(
    answers: List<VisibleAnswer>,
    widget: QuestionWidget,
    item: @Composable (VisibleAnswer) -> Unit,
    afterScene: @Composable () -> Unit,
) {
    val grouped = widget.categories.associateWith { category ->
        answers.filter { it.content.toCategorizedAnswer()?.category == category }
    }
    CategoryScene(widget, counts = grouped.mapValues { it.value.size })
    afterScene()
    var position = 0
    grouped.forEach { (category, categoryAnswers) ->
        if (categoryAnswers.isEmpty()) return@forEach
        CategoryHeader(category, categoryAnswers.size)
        categoryAnswers.forEach { answer ->
            val staggerIndex = position++
            key(answer.id) { EnteringAnswer(category, staggerIndex) { item(answer) } }
        }
    }
    answers.filter { it.content.toCategorizedAnswer()?.category !in widget.categories }
        .forEach { answer -> key(answer.id) { item(answer) } }
}

@Composable
private fun CategoryHeader(category: AnswerCategory, count: Int) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Box(Modifier.size(width = 4.dp, height = 24.dp).background(category.accentColor, RoundedCornerShape(2.dp)))
        Text(
            "${category.emoji} ${category.label()}",
            modifier = Modifier.weight(1f),
            style = MaterialTheme.typography.titleMedium,
        )
        Surface(shape = RoundedCornerShape(50), color = category.accentColor, contentColor = MaterialTheme.colorScheme.surface) {
            Text(
                count.toString(),
                modifier = Modifier.padding(horizontal = 10.dp, vertical = 2.dp),
                style = MaterialTheme.typography.labelLarge,
            )
        }
    }
}

private val AnswerCategory.entryDirection: IntOffset
    get() = when (this) {
        AnswerCategory.WIND -> IntOffset(-1, 0)
        AnswerCategory.ANCHOR -> IntOffset(0, -1)
        AnswerCategory.ROCK -> IntOffset(0, 1)
        AnswerCategory.ISLAND -> IntOffset(1, 0)
        else -> if (ordinal % 2 == 0) IntOffset(-1, 0) else IntOffset(1, 0)
    }

@Composable
private fun EnteringAnswer(category: AnswerCategory, index: Int, content: @Composable () -> Unit) {
    val visibleState = remember { MutableTransitionState(false) }.apply { targetState = true }
    val delay = minOf(index, MAX_STAGGER_STEPS) * STAGGER_MILLIS
    val direction = category.entryDirection
    AnimatedVisibility(
        visibleState = visibleState,
        enter = fadeIn(tween(350, delayMillis = delay)) +
            slideIn(tween(450, delayMillis = delay, easing = FastOutSlowInEasing)) { size ->
                IntOffset(direction.x * size.width / 3, direction.y * size.height)
            },
    ) {
        content()
    }
}

@Composable
private fun RotiHistogram(answers: List<VisibleAnswer>) {
    val stats = answers.rotiStats() ?: return
    val maxCount = stats.counts.max()
    val progress = remember { Animatable(0f) }
    LaunchedEffect(Unit) { progress.animateTo(1f, spring(dampingRatio = .55f, stiffness = 120f)) }
    val barColor = MaterialTheme.colorScheme.primary
    val trackColor = MaterialTheme.colorScheme.surfaceVariant
    val shownAverage = stats.average * progress.value.coerceIn(0f, 1f)
    ElevatedCard(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            Row(verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                Text(
                    stringResource(Res.string.roti_average, shownAverage.formatOneDecimal()),
                    style = MaterialTheme.typography.headlineMedium,
                    color = MaterialTheme.colorScheme.primary,
                )
                Text(
                    if (stats.votes == 1) {
                        stringResource(Res.string.roti_vote_single)
                    } else {
                        stringResource(Res.string.roti_votes, stats.votes)
                    },
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            RotiGauge(stats.average, Modifier.padding(horizontal = 24.dp))
            Row(
                modifier = Modifier.fillMaxWidth().height(160.dp),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                verticalAlignment = Alignment.Bottom,
            ) {
                stats.counts.forEachIndexed { index, count ->
                    Column(
                        modifier = Modifier.weight(1f).fillMaxHeight(),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(4.dp, Alignment.Bottom),
                    ) {
                        Text(count.toString(), style = MaterialTheme.typography.labelLarge)
                        Canvas(Modifier.fillMaxWidth().weight(1f)) {
                            val radius = CornerRadius(8.dp.toPx())
                            drawRoundRect(trackColor, cornerRadius = radius)
                            val barHeight = (size.height * count / maxCount * progress.value).coerceIn(0f, size.height)
                            drawRoundRect(
                                barColor,
                                topLeft = Offset(0f, size.height - barHeight),
                                size = Size(size.width, barHeight),
                                cornerRadius = radius,
                            )
                        }
                        Text(rotiEmojis[index], fontSize = 22.sp)
                    }
                }
            }
        }
    }
}
